import { useCallback, useMemo, useState } from 'react';
import type { RegistroDescartado } from '../../api/sincronizacao.js';
import { contagemDoRecorte, intervaloDaPagina, totalDePaginas } from '../../api/paginacao.js';
import { seInformado } from '../../api/recorte.js';
import { listarDescartados } from '../../api/sincronizacao.js';
import type { FiltroDeDescartados } from '../../api/sincronizacao.js';
import { useConsulta } from '../../api/useConsulta.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { filialDoFiltro, rotuloDaFilial } from '../../componentes/deposito.js';
import { EstadoVazio } from '../../componentes/EstadoVazio.js';
import { FaixaDeFiltros } from '../../componentes/FaixaDeFiltros.js';
import { CampoFilial, ContadorRegistros, FiltroSelecao } from '../../componentes/Filtros.js';
import { IconeConfere } from '../../componentes/Icones.js';
import { Paginacao } from '../../componentes/Paginacao.js';
import { Resultado } from '../../componentes/Resultado.js';
import { Tabela } from '../../componentes/Tabela.js';
import type { Coluna } from '../../componentes/Tabela.js';
import {
  identificacaoEmTexto,
  OPCOES_DE_TIPO,
  rotuloDoTipoDeLote,
  TIPOS_DE_LOTE,
  TODOS,
  valorDoSeletor,
} from './situacao.js';

const COLUNAS: readonly Coluna<RegistroDescartado>[] = [
  {
    chave: 'registro',
    rotulo: 'Registro no ERP',
    largura: '30%',
    celula: (descartado) => (
      <span className="sincronizacao__pilha">
        <span className="sincronizacao__codigo">
          {identificacaoEmTexto(descartado.identificacao)}
        </span>
        <span className="sincronizacao__discreto">{rotuloDoTipoDeLote(descartado.tipo)}</span>
      </span>
    ),
  },
  {
    chave: 'motivo',
    rotulo: 'Motivo',
    largura: '30%',
    celula: (descartado) => <span>{descartado.motivo}</span>,
  },
  {
    chave: 'origem',
    rotulo: 'Filial · lote',
    largura: '16%',
    celula: (descartado) => (
      <span className="sincronizacao__pilha">
        <span>{rotuloDaFilial(descartado.codFil)}</span>
        <span className="sincronizacao__discreto mono">Lote {descartado.numeroLote}</span>
      </span>
    ),
  },
  {
    chave: 'registrado',
    rotulo: 'Registrado em',
    celula: (descartado) => (
      <span className="sincronizacao__discreto">{formatarDataHora(descartado.registradoEm)}</span>
    ),
  },
];

/**
 * O que veio do ERP e não entrou: depósito desconhecido, produto sem
 * cadastro, campo obrigatório vazio. O registro não some em silêncio — fica
 * aqui com o motivo e os campos para conferir no ERP.
 */
export function AbaDescartados() {
  const [tipo, definirTipo] = useState(TODOS);
  const [filial, definirFilial] = useState('');
  const [pagina, definirPagina] = useState(1);
  const codFil = filialDoFiltro(filial);

  const filtro = useMemo<FiltroDeDescartados>(
    () => ({
      pagina,
      ...seInformado('tipo', valorDoSeletor(tipo, TIPOS_DE_LOTE)),
      ...seInformado('codFil', codFil),
    }),
    [pagina, tipo, codFil],
  );

  const buscar = useCallback(() => listarDescartados(filtro), [filtro]);
  const consulta = useConsulta(buscar);

  function recortar(aplicar: () => void) {
    aplicar();
    definirPagina(1);
  }

  return (
    <div className="sincronizacao__aba">
      <FaixaDeFiltros rotulo="Filtros dos descartados">
        <FiltroSelecao
          rotulo="Tipo"
          valor={tipo}
          opcoes={OPCOES_DE_TIPO}
          aoMudar={(valor) => {
            recortar(() => {
              definirTipo(valor);
            });
          }}
        />
        <CampoFilial
          valor={filial}
          aoMudar={(valor) => {
            recortar(() => {
              definirFilial(valor);
            });
          }}
        />
        {consulta.dados !== null && (
          <ContadorRegistros>
            {contagemDoRecorte(consulta.dados.total, 'registro', 'registros')}
          </ContadorRegistros>
        )}
      </FaixaDeFiltros>

      <Resultado
        consulta={consulta}
        espera="Carregando os registros descartados…"
        estaVazio={(dados) => dados.itens.length === 0}
        vazio={
          <EstadoVazio
            icone={<IconeConfere tamanho={28} />}
            titulo="Nenhum registro descartado neste recorte."
            detalhe="Tudo o que o ERP mandou foi aceito. Registro recusado aparece aqui com o motivo."
          />
        }
      >
        {(dados) => (
          <Tabela
            itens={dados.itens}
            colunas={COLUNAS}
            chaveDe={(descartado) => descartado.id}
            descricao="Registros descartados"
            rodape={
              <Paginacao
                intervalo={intervaloDaPagina(dados, 'registro', 'registros')}
                pagina={dados.pagina}
                totalPaginas={totalDePaginas(dados)}
                aoIrPara={definirPagina}
              />
            }
          />
        )}
      </Resultado>
    </div>
  );
}
